/**
 * @file University.page.jsx
 * @description USABG University main page component
 *
 * This page provides the complete interface for the USABG University training program:
 * - Welcome section with logo, video, and introduction
 * - MyEnrollment: Shows current user's enrollment status and actions
 * - Signup: Displays available sessions for self-enrollment
 * - Invitor: Allows managers to invite downline agents
 * - Calendar: Lists all upcoming sessions with availability
 * - AdminPanel: Session and location management (admin only)
 *
 * @see /docs/modules/UsabgUniversity/README.md for full documentation
 */
import React from 'react'
import DashboardLayout from './../../components/Admin/DashboardLayout'
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody } from 'mdb-react-ui-kit'
import PageHeading from './../../components/shared/PageHeading.component'
import ContentHtml from './../../components/content/ContentHtml/ContentHtml.component'
import ContentVideo from './../../components/content/ContentVideo/ContentVideo.component'
import Calendar from './components/Calendar/Calendar.component'
import Signup from './components/Signup/Signup.component'
import MyEnrollment from './components/MyEnrollment/MyEnrollment.component'
import Invitor from './components/Invitor/Invitor.component'
import AdminPanel from './components/AdminPanel/AdminPanel.component'
import UniversityLogo from './../../assets/images/usabg-university-logo.png'

import './University.page.scss'

/**
 * UniversityPage Component
 *
 * Main page for USABG University training program management.
 * Renders different components based on user role and enrollment status.
 *
 * @component
 * @example
 * // Route: /usabg-university
 * <UniversityPage />
 */
class UniversityPage extends React.Component {
  state = {
    loading: true,
  }

  componentDidMount() {
    this.setState({ loading: false })
  }

  render() {
    return (
      <DashboardLayout>
        <main id='UniversityPage' className='adminStyle mainSection mt-5 pb-5'>
          <MDBContainer fluid className='mt-2'>
            {/* Page Header */}
            <PageHeading label='USABG University' />

            {/* Row 1: Logo and Welcome Video */}
            <MDBRow className='mb-4'>
              {/* Logo Column */}
              <MDBCol lg='4' md='12' className='mb-3 mb-lg-0'>
                <MDBCard className='h-100'>
                  <MDBCardBody className='d-flex align-items-center justify-content-center'>
                    <div className='university-logo-container'>
                      <img src={UniversityLogo} alt='USABG University' className='university-logo' />
                    </div>
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>

              {/* Welcome Video Column */}
              <MDBCol lg='8' md='12'>
                <MDBCard className='h-100'>
                  <MDBCardBody className='welcome-video-container'>
                    <ContentVideo contentSlug='usabg-university-welcome-video' />
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            </MDBRow>

            {/* Row 2: Introduction Paragraph */}
            <MDBRow className='mb-4'>
              <MDBCol size='12'>
                <MDBCard>
                  <MDBCardBody className='welcome-intro-container'>
                    <ContentHtml contentSlug='usabg-university-welcome-intro' />
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            </MDBRow>

            {/* My Enrollment */}
            <MDBRow className='mb-4'>
              <MDBCol size='12'>
                <MyEnrollment />
              </MDBCol>
            </MDBRow>

            {/* Signup Component */}
            <MDBRow className='mb-4'>
              <MDBCol size='12'>
                <Signup />
              </MDBCol>
            </MDBRow>

            {/* Invitor Component */}
            <MDBRow className='mb-4'>
              <MDBCol size='12'>
                <Invitor />
              </MDBCol>
            </MDBRow>

            {/* Calendar Component */}
            <MDBRow className='mb-4'>
              <MDBCol size='12'>
                <Calendar />
              </MDBCol>
            </MDBRow>

            {/* Admin Panel */}
            <MDBRow className='mb-4'>
              <MDBCol size='12'>
                <AdminPanel />
              </MDBCol>
            </MDBRow>
          </MDBContainer>
        </main>
      </DashboardLayout>
    )
  }
}

export default UniversityPage
